import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { logger } from "../../../../../../shared/core/logging/logger";
import type { Card } from "../../../../../../shared/domains/cards/card";
import { CardStatus, OpposeCardReason } from "../../../../../../shared/domains/cards/card";
import { CARD_OPPOSITION_MODAL_ID } from "../../../../../core/modal/modal-id";
import { BluePrimaryButton, WhitePrimaryButton } from "../../../../common/buttons/primary-button";
import { ErrorMessage } from "../../../../common/error-message";
import type { RadioSelectorType } from "../../../../common/forms/radio-selector";
import { RadioSelector } from "../../../../common/forms/radio-selector";
import { Modal } from "../../../../common/modal/modal";
import { WhiteModalContainer } from "../../../../common/modal/white-modal-container";
import { StaticAssets } from "../../../../styles/custom-assets";
import { theme } from "../../../../styles/theme";
import { ActionMenu } from "../action-menu";

interface OppositionCardProps {
  card: Card;
  loading: boolean;
  oppose: (reason: OpposeCardReason) => Promise<void>;
  className?: string;
}

export const OppositionButton: React.FC<OppositionCardProps> = ({ card, className, ...otherOptions }) => {
  const { formatMessage } = useIntl();

  const canOppose = useMemo(() => {
    switch (card.status) {
      case CardStatus.Pending:
      case CardStatus.Active:
      case CardStatus.Blocked:
        return true;
      default:
        return false;
    }
  }, [card.status]);

  if (!canOppose) {
    return null;
  }

  return (
    <ActionMenu
      title={formatMessage("cardOptions.oppositionCard.opposeButton")}
      description={formatMessage("cardOptions.oppositionCard.opposeDescription")}
      iconSrc={StaticAssets.cardOpposition}
      className={className}
      onClick={() => {
        Modal.present(CARD_OPPOSITION_MODAL_ID, () => <OppositionModal card={card} {...otherOptions} />, {
          canBeDismissed: false,
        });
      }}
    />
  );
};

const OppositionModal = (props: OppositionCardProps) => (
  <WhiteModalContainer closeButton id={CARD_OPPOSITION_MODAL_ID}>
    <OppositionCard {...props} />
  </WhiteModalContainer>
);

const OppositionCard: React.FC<OppositionCardProps> = (props) => {
  const { card, loading, oppose, className } = props;
  const { formatMessage } = useIntl();
  const [error, setError] = useState<string | null>(null);
  const [selectedOpposeReason, setSelectedOpposeReason] = useState(OpposeCardReason.Lost);

  useEffect(() => {
    setSelectedOpposeReason(OpposeCardReason.Lost);
  }, [card]);

  const opposeReasons = useMemo(
    () =>
      new Map<OpposeCardReason, string>([
        [OpposeCardReason.Lost, formatMessage("cardOptions.oppositionCard.reasonLost")],
        [OpposeCardReason.Stolen, formatMessage("cardOptions.oppositionCard.reasonStolen")],
        [OpposeCardReason.NotDistributed, formatMessage("cardOptions.oppositionCard.reasonNotDistributed")],
      ]),
    [formatMessage],
  );

  const opposeCard = async () => {
    try {
      setError(null);
      await oppose(selectedOpposeReason);
      Modal.dismiss(CARD_OPPOSITION_MODAL_ID);
    } catch (e) {
      logger.debug(e);
      setError(e?.response?.data?.error?.message);
    }
  };

  const opposeCardWithConfirm = () => {
    if (window?.confirm) {
      if (
        window.confirm(
          `${formatMessage("cardOptions.oppositionCard.opposePromptTitle")}. ${formatMessage(
            "cardOptions.oppositionCard.opposePromptMessage",
          )}`,
        )
      ) {
        opposeCard();
      } else {
        Modal.dismiss(CARD_OPPOSITION_MODAL_ID);
      }
    } else {
      opposeCard();
    }
  };

  return (
    <Container className={className}>
      <Illustration src={StaticAssets.cardOpposition} alt="" />
      <Title>{formatMessage("cardOptions.oppositionCard.opposePromptTitle")}</Title>
      <MessageLabel>{formatMessage("cardOptions.oppositionCard.title")}</MessageLabel>
      <StyledRadioSelector
        innerId="opposition_button"
        values={opposeReasons}
        value={selectedOpposeReason}
        onChange={setSelectedOpposeReason}
      />
      {error && <StyledErrorMessage>{error}</StyledErrorMessage>}
      <ConfirmButtonsContainer>
        <ConfirmButton showSpinner={loading} size="XS" onClick={() => !loading && opposeCardWithConfirm()}>
          {formatMessage("cardOptions.oppositionCard.opposeConfirmButton")}
        </ConfirmButton>
        <CancelButton size="XS" onClick={() => Modal.dismiss(CARD_OPPOSITION_MODAL_ID)}>
          {formatMessage("cardOptions.oppositionCard.opposeCancelButton")}
        </CancelButton>
      </ConfirmButtonsContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Illustration = styled.img`
  width: 56px;
  height: 56px;
`;

const Title = styled.h2`
  ${theme.mediumText};
  margin: 16px 0 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const MessageLabel = styled.span`
  ${theme.text};
  margin-top: 8px;
  font-size: 14px;
  line-height: 19px;
  color: ${theme.colors.gray[800]};
`;

const ConfirmButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const ConfirmButton = styled(BluePrimaryButton)`
  width: 100%;
  padding: 13px;
  box-shadow: none;
`;

/* Outlined, not a plain white fill: DefaultButton clears the border, so it is set back here. */
const CancelButton = styled(WhitePrimaryButton)`
  width: 100%;
  padding: 13px;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};

  &:focus,
  &:active,
  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;

/* One reason per line, each on its own row of the list. */
const StyledRadioSelector = styled<RadioSelectorType<OpposeCardReason>>(RadioSelector)`
  flex-direction: column;
  margin-top: 20px;

  > * {
    padding: 14px 0;
    border-top: 1px solid ${theme.colors.gray[100]};
  }

  > *:first-child {
    border-top: 0;
  }

  label {
    ${theme.text};
    flex: 1;
    font-size: 16px;
    line-height: 22px;
    color: ${theme.colors.blue[800]};
  }

  input {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-color: ${theme.colors.blue[700]};
  }

  input:checked::after {
    width: 10px;
    height: 10px;
    top: 5px;
    left: 5px;
    background-color: ${theme.colors.blue[700]};
  }
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin: 16px 0 0;
`;
